import { utils, query } from '@kurtosys/ksys-app-template';
import { computed, action, observable } from 'mobx';
import { IFundCardConfiguration } from '../models';
import { IGridProps } from '@kurtosys/ksys-app-components/dist/components/base/Grid/models';
import { IGridComponentReference } from '@kurtosys/ksys-app-components/dist/components/base/Grid/models/IGridComponentReference';
import { SummaryBlocksStore } from '../../SummaryBlocks/stores/SummaryBlocksStore';
import { ChildDropdownStore } from '../../ChildDropdown/stores/ChildDropdownStore';
import { FactListStore } from '../../FactList/stores/FactListStore';
import { RedirectButtonStore } from '../../RedirectButton/stores/RedirectButtonStore';
import SummaryBlocks from '../../SummaryBlocks';
import ChildDropdown from '../../ChildDropdown';
import DocumentDownload from '../../DocumentDownload';
import FactList from '../../FactList';
import RedirectButton from '../../RedirectButton';
import DefinitionList from '../../DefinitionList';
import { StoreContext } from '../../../configuration/StoreContext';
import { QueryStore } from '../../App/stores/QueryStore';
import { IConfigurationDataEntityMapping } from '../../../models/app/IConfigurationDataEntityMapping';
import { StoreBase } from '../../../common/StoreBase';
import { IQueryOptions, IExecutionOptions, IEntity } from '../../../models/commonTypes';
import { IConfigurationComponentOrchestration } from '../../../models/app/IConfigurationComponentOrchestration';
import { DocumentDownloadStore } from '../../DocumentDownload/stores/DocumentDownloadStore';
import { DefinitionListStore } from '../../DefinitionList/stores/DefinitionListStore';

const deepMergeObjects = utils.object.deepMergeObjects;
const Query = query.Query;

export class FundCardStore extends StoreBase {
	static componentKey: 'fundCard' = 'fundCard';
	@observable.ref rootEntity: IEntity;
	@observable.ref rawChildEntity: IEntity | undefined;
	@observable.ref childEntities: IEntity[] = [];
	queryStore: QueryStore;

	constructor(storeContext: StoreContext, rootEntity: IEntity, childEntities: IEntity[] = []) {
		super(storeContext);
		this.queryStore = new QueryStore(storeContext);
		this.rootEntity = rootEntity;
		this.childEntities = childEntities;
		this.childEntity = this.getInitialChildEntity();
	}

	@computed
	get configuration(): IFundCardConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(FundCardStore.componentKey);
		}
	}

	@action
	async initialize(): Promise<void> {

	}

	@computed
	get entityMapping(): IConfigurationDataEntityMapping {
		const { appStore } = this.storeContext;
		return appStore.entityMapping;
	}

	loadQueryStoreContext = (): void => {
		const { queryStore } = this.storeContext;
		const entityMapping = this.entityMapping;
		const { rootEntityType, childEntityType } = entityMapping;
		this.queryStore.context = {
			...(queryStore.context || {}),
			entityByType: {
				[rootEntityType]: this.rootEntity,
			},
		};
		if (childEntityType && this.childEntity && this.queryStore.context.entityByType) {
			this.queryStore.context.entityByType[childEntityType] = this.childEntity;
		}


	}

	@computed
	get childEntity(): IEntity | undefined {
		return this.rawChildEntity;
	}

	set childEntity(value: IEntity | undefined) {
		this.rawChildEntity = value;
		this.loadQueryStoreContext();
	}



	@action
	getInitialChildEntity(): IEntity | undefined {
		if (this.childEntities && this.childEntities.length > 0) {
			return this.childEntities[0];
		}
	}

	@computed
	get gridProps(): IGridProps | undefined {
		const gridProps = this.configuration && this.configuration.gridProps;
		return gridProps;
	}

	@computed
	get stores() {
		return {
			summaryBlocks: new SummaryBlocksStore(this.storeContext, this),
			childDropdown: new ChildDropdownStore(this.storeContext, this),
			documentDownload: new DocumentDownloadStore(this.storeContext, this),
			factList: new FactListStore(this.storeContext, this),
			redirectButton: new RedirectButtonStore(this.storeContext, this),
			definitionList: new DefinitionListStore(this.storeContext, this),
		};
	}

	@computed
	get components(): { [key: string]: IGridComponentReference; } {
		return {
			'components.summaryBlocks': {
				key: 'components.summaryBlocks',
				component: SummaryBlocks,
				props: {
					summaryBlocksStore: this.stores.summaryBlocks,
				},
			},
			'components.childDropdown': {
				key: 'components.childDropdown',
				component: ChildDropdown,
				props: {
					childDropdownStore: this.stores.childDropdown,
				},
			},
			'components.definitionList': {
				key: 'components.definitionList',
				component: DefinitionList,
				props: {
					definitionListStore: this.stores.definitionList,
				},
			},
			'components.documentDownload': {
				key: 'components.documentDownload',
				component: DocumentDownload,
				props: {
					documentDownloadStore: this.stores.documentDownload,
				},
			},
			'components.factList': {
				key: 'components.factList',
				component: FactList,
				props: {
					factListStore: this.stores.factList,
				},
			},
			'components.redirectButton': {
				key: 'components.redirectButton',
				component: RedirectButton,
				props: {
					redirectButtonStore: this.stores.redirectButton,
				},
			},
		};
	}

	getQueryValue(queryOptions?: IQueryOptions, responseForContext?: object, overrideExecutionOptions?: Partial<IExecutionOptions>) {
		if (queryOptions) {
			const responseContextExecutionOptions: Partial<IExecutionOptions> = {
				context: {
					response: responseForContext,
				},
			};
			overrideExecutionOptions = deepMergeObjects(
				responseContextExecutionOptions,
				overrideExecutionOptions || {},
			);
			return this.queryStore.query(queryOptions, overrideExecutionOptions);
		}
		return;
	}

	mergeQueriesAndProps<T>(componentOrchestration?: IConfigurationComponentOrchestration): T | undefined {
		if (componentOrchestration) {
			const { props, queries } = componentOrchestration;
			const queryResults = queries ? Query.convertQueryMime(queries, this.queryStore.executionOptions) : {};
			const response: T = deepMergeObjects(props || {}, queryResults);
			return response;
		}
	}

}